import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { AppBar, Button, Grid, Toolbar } from '@material-ui/core';
import {
  AccountBalance,
  Assessment,
  Check,
  ExitToApp,
  Person,
  QuestionAnswer,
  ShowChart,
  TrendingDown,
  Help,
} from '@material-ui/icons';

import * as styles from './Header.css';
import { AscendState } from '../../../ducks';
import { LOGGING_OUT } from '../../../ducks/actions';
import { NavLink } from '../../../models/nav';
import history from '../../../history';
import logo from '../../shared/whiteLogo.png';
import MobileDropdown from './MobileDropdown/MobileDropdown';
import { getAllDebtsData } from '../../../ducks/debt';

type StateProps = {
  isMobile: boolean | undefined;
  route: string | null;
  signupMapping: string;
  lastQuizUrl: string;
  lastResultsUrl: string;
  debts: any[];
};
type DispatchProps = {
  logout: () => void;
};
type HeaderProps = StateProps &
  DispatchProps & {
    submittedAgreement: boolean;
    freshStartFromCalc: boolean;
  };

const CustomNavLink = ({ name, icon, link, action }: NavLink) => (
  <Button
    onClick={action ? () => action() : undefined}
    component={link ? (props: any) => <Link to={link} {...props} /> : undefined}>
    <span className={styles.navIcon}>{icon}</span>
    <span className={styles.navText}>{name}</span>
  </Button>
);

const Header = (
  {
    isMobile,
    logout,
    route,
    submittedAgreement,
    signupMapping,
    freshStartFromCalc,
    lastQuizUrl,
    lastResultsUrl,
    debts,
  }: HeaderProps,
  showTab: boolean
) => {
  const navLinks: NavLink[] = useMemo(
    () => [
      {
        name: 'Start Here',
        icon: <Check />,
        link: '/startHere',
      },
      {
        name: 'Dashboard',
        icon: <ShowChart />,
        link: '/home',
      },
      // TODO: Add back once CFT Pay is complete
      // {
      //   name: 'Billing',
      //   icon: <AttachMoney />,
      //   link: '/billing'
      // },
      {
        name: 'Quiz Results',
        icon: <Assessment />,
        link: '/quizResults',
      },
      {
        name: 'Spending Tracker',
        icon: <AccountBalance />,
        link: '/spendingTracker',
      },
      {
        name: 'Debt Payoff',
        icon: <TrendingDown />,
        link: '/debtPayoffTracker',
      },
      {
        name: 'Credit',
        icon: <Assessment />,
        link: '/creditReport',
      },
      {
        name: 'Your Analyst',
        icon: <Person />,
        link: '/yourAnalyst',
      },
      {
        name: 'Help',
        icon: <Help />,
        link: '/helpPage',
      },
      // {
      //   name: "FAQ's",
      //   icon: <QuestionAnswer />,
      //   link: '/faq',
      // },
      {
        name: 'Logout',
        icon: <ExitToApp />,
        action: () => logout(),
      },
    ],
    [logout]
  );

  const filteredNavLinks = useMemo(() => {
    // if signupMapping equals "flowBasicFreshStartE", remove specific nav links
    let filtered = navLinks;

    if (!lastQuizUrl || !lastResultsUrl) {
      filtered = filtered.filter((link) => link.name !== 'Quiz Results');
    }

    // Hide Dashboard if user has no debts (overrides all other conditions)
    const hasNoDebts = !debts || debts.length === 0;
    if (hasNoDebts) {
      filtered = filtered.filter((link) => link.name !== 'Dashboard');
    }

    if (signupMapping === 'flowBasicFreshStartE') {
      filtered = filtered.filter(
        (link) => !['Start Here', 'Dashboard', 'Credit', 'Your Analyst'].includes(link.name)
      );
    } else if (freshStartFromCalc) {
      filtered = filtered.filter(
        (link) =>
          !['Start Here', 'Dashboard', 'Credit', 'Your Analyst', 'Debt Payoff'].includes(link.name)
      );
    } else {
      //csa route of people (ben wanted to hide start here and payoff from csa people)
      filtered = filtered.filter((link) => !['Start Here', 'Debt Payoff'].includes(link.name));
    }
    // Otherwise, use the previous condition based on submittedAgreement
    return submittedAgreement
      ? filtered.filter((link) => !['Start Here', 'Debt Payoff', 'Credit'].includes(link.name))
      : filtered;
  }, [navLinks, signupMapping, submittedAgreement, lastQuizUrl, lastResultsUrl, debts, freshStartFromCalc]);

  return (
    <AppBar position="static" className={route !== '/onboard' ? styles.fixedHeader : ''}>
      <Toolbar>
        <Grid container alignItems="center">
          <Grid
            item
            xs={10}
            sm={2}
            container
            wrap="nowrap"
            onClick={() => route !== '/onboard' && history.push('/home')}>
            <Grid item>
              <img
                className={styles.logo}
                style={route !== '/onboard' ? { cursor: 'pointer' } : {}}
                src={logo}
              />
            </Grid>
            <Grid item>
              <h1
                className={styles.title}
                style={route !== '/onboard' ? { cursor: 'pointer' } : {}}>
                Ascend
              </h1>
            </Grid>
          </Grid>
          <Grid item xs={2} sm={10} container justify="flex-end" alignItems="center">
            {route !== '/onboard' && (
              <>
                {isMobile ? (
                  <MobileDropdown navLinks={filteredNavLinks} />
                ) : (
                  <Grid container justify="flex-end" wrap="nowrap">
                    {filteredNavLinks.map((linkData) => (
                      <CustomNavLink key={linkData.name} {...linkData} />
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

const mapStateToProps = (state: AscendState) => ({
  signupMapping: state.user.data.signupMapping,
  lastQuizUrl: state.user.data.lastQuizUrl,
  lastResultsUrl: state.user.data.lastResultsUrl,
  isMobile: state.layout.isMobile,
  route: state.router.location && state.router.location.pathname,
  submittedAgreement: state.user.data.submittedAgreement,
  freshStartFromCalc: state.user.data.freshStartFromCalc,
  debts: getAllDebtsData(state),
});

// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch: Dispatch) => ({
  logout: () => dispatch({ type: LOGGING_OUT }),
});

export default connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(Header);
